import { useQuery } from '@tanstack/react-query'

// Counter for the FOUNDING coupon used by the paywall card. Backed by
// /api/founding-status (a Vercel function that hits Stripe and caches
// the response in-process for 60s). React Query layers another 5 min
// staleTime on top so the paywall doesn't fan out a request on every
// render. retry: 1 because a flaky Stripe call shouldn't kill the page;
// we'd rather show only annual + monthly than render a stale or zero
// counter.

interface FoundingStatusData {
  claimed:   number
  total:     number
  remaining: number
  available: boolean
}

export interface FoundingStatus extends FoundingStatusData {
  isLoading: boolean
}

export function useFoundingStatus(): FoundingStatus {
  const { data, isLoading } = useQuery<FoundingStatusData>({
    queryKey:  ['founding-status'],
    queryFn:   async () => {
      const resp = await fetch('/api/founding-status')
      if (!resp.ok) throw new Error(`founding-status HTTP ${resp.status}`)
      return resp.json() as Promise<FoundingStatusData>
    },
    staleTime: 5 * 60 * 1000,
    retry:     1,
  })

  // Fail closed — on error / pending, available is false so the paywall
  // hides the card cleanly. Loading consumers can read isLoading and
  // render a skeleton in place to avoid layout shift.
  return {
    claimed:   data?.claimed   ?? 0,
    total:     data?.total     ?? 0,
    remaining: data?.remaining ?? 0,
    available: data?.available ?? false,
    isLoading,
  }
}
